import { useQuery, useSuspenseQuery, type UseQueryOptions } from "@tanstack/react-query";
import { axiosClient as client, useAxios } from "auth-sdk";
import type { FundingApplicationSchemas } from "api-types";
import { useSearchParams } from "react-router";
import type { AxiosError, AxiosResponse } from "axios";

import { AuthCallbackPath } from "../../api-types/connector-callbacks";
import {
  FUNDING_APPLICATION_COMPLETE_CONNECTION_API,
  FUNDING_APPLICATION_CONNECTORS_API,
} from "../../components/constants";
import { ConnectorCategory } from "../../types";

export const usePlatformConnectors = (
  customerDomain: string,
  params?: { category?: ConnectorCategory; skipConnections?: boolean; callbackPath?: AuthCallbackPath }
) => {
  const client = useAxios();
  const fetchUrl = FUNDING_APPLICATION_CONNECTORS_API.replace(":customerSlug", customerDomain);

  // create the query function
  const queryFn = () => client.get<FundingApplicationSchemas.ConnectorsListSchema>(fetchUrl, { params });
  return useSuspenseQuery({
    queryKey: [fetchUrl, params],
    queryFn,
    select: ({ data }) => data.connectors,
    refetchOnMount: "always",
  });
};

export const useCompleteConnectionProps = (
  customerDomain: string,
  options: Omit<
    UseQueryOptions<
      AxiosResponse<FundingApplicationSchemas.CompleteConnectionPropsSchema>,
      AxiosError,
      FundingApplicationSchemas.CompleteConnectionPropsSchema
    >,
    "queryKey" | "queryFn" | "select" | "refetchOnMount"
  > = {}
) => {
  const [searchParams] = useSearchParams();
  const fetchUrl = FUNDING_APPLICATION_COMPLETE_CONNECTION_API.replace(":customerSlug", customerDomain);
  //Retain the original search params as these are used on BE
  const queryFn = () =>
    client.get<FundingApplicationSchemas.CompleteConnectionPropsSchema>(fetchUrl, { params: searchParams });
  return useQuery({
    ...options,
    queryKey: [fetchUrl, searchParams.toString()],
    queryFn,
    select: ({ data }) => data,
    refetchOnMount: "always",
  });
};
